/**
 * Types for the asset upload system
 */

// Asset type detected from MIME type
export type AssetType = "audio" | "video" | "image";

// Audio role options (single-select for audio assets)
export type AudioRole = "SFX" | "Dialogue" | "Music";

// Visual motion options (single-select for visual assets)
export type VisualMotion = "Static" | "Motion";

// Tags that can be assigned to an uploading asset
export interface AssetTags {
  scene: string | null;
  category: string[];
  role: AudioRole | null; // audio only
  motion: VisualMotion | null; // visual only
}

// Upload status
export type UploadStatus = "pending" | "uploading" | "complete" | "error";

// An asset in the upload queue
export interface UploadingAsset {
  id: string;
  file: File;
  status: UploadStatus;
  progress?: number;
  error?: string;
  type: AssetType;
  thumbnail?: string;
  title: string;
  description: string;
  tags: AssetTags;
}

// Role options with display labels
export const AUDIO_ROLE_OPTIONS: { id: AudioRole; label: string }[] = [
  { id: "SFX", label: "SFX" },
  { id: "Dialogue", label: "Dialogue" },
  { id: "Music", label: "Music" },
];

// Motion options with display labels
export const VISUAL_MOTION_OPTIONS: { id: VisualMotion; label: string }[] = [
  { id: "Static", label: "Static" },
  { id: "Motion", label: "Motion" },
];

/**
 * Detect asset type from MIME type
 */
export function detectAssetType(file: File): AssetType {
  if (file.type.startsWith("audio/")) {
    return "audio";
  }
  if (file.type.startsWith("video/")) {
    return "video";
  }
  return "image";
}

/**
 * Generate a mock AI title from filename
 */
export function generateMockTitle(file: File): string {
  const name = file.name.replace(/\.[^/.]+$/, "");
  return name.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Generate a mock AI description from file
 */
export function generateMockDescription(file: File): string {
  const type = detectAssetType(file);
  const sizeMB = (file.size / 1024 / 1024).toFixed(1);
  return `${type.charAt(0).toUpperCase() + type.slice(1)} asset, ${sizeMB}MB`;
}

/**
 * Create default tags for an asset
 */
export function createDefaultTags(_type: AssetType): AssetTags {
  return {
    scene: null,
    category: [],
    role: null,
    motion: null,
  };
}

/**
 * Check if a file is a supported asset type
 */
export function isSupportedAssetType(file: File): boolean {
  return (
    file.type.startsWith("audio/") ||
    file.type.startsWith("video/") ||
    file.type.startsWith("image/")
  );
}
